import { useQueryClient } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { cn } from '@/lib/utils';
import { useNetworkStore } from '@/store/network.store';
import { STATUS_COLOR_CLASS } from '@/styles/statusColors';
import {
  MODE_STYLES,
  RECONNECTED_DISPLAY_MS,
  RECOVERY_BANNER_FOR,
  type BannerMode,
  type ProblemMode,
} from './NetworkStatusBanner.constants';

function resolveProblemMode(isOnline: boolean, serverReachable: boolean): ProblemMode | null {
  if (!isOnline) return 'offline';
  if (!serverReachable) return 'unreachable';
  return null;
}

export function NetworkStatusBanner() {
  const isOnline = useOnlineStatus();
  const serverReachable = useNetworkStore((s) => s.serverReachable);
  const queryClient = useQueryClient();

  const problem = resolveProblemMode(isOnline, serverReachable);
  const [syncedProblem, setSyncedProblem] = useState<ProblemMode | null>(problem);
  const [lastProblem, setLastProblem] = useState<ProblemMode | null>(problem);
  const [mode, setMode] = useState<BannerMode>(problem ?? 'offline');
  const [visible, setVisible] = useState(problem !== null);

  if (problem !== syncedProblem) {
    setSyncedProblem(problem);
    if (problem !== null) {
      setLastProblem(problem);
      setMode(problem);
      setVisible(true);
    } else if (lastProblem !== null) {
      setMode(RECOVERY_BANNER_FOR[lastProblem]);
      setLastProblem(null);
      setVisible(true);
    }
  }

  useEffect(() => {
    if (problem !== null || !visible) return;
    const timer = window.setTimeout(() => setVisible(false), RECONNECTED_DISPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [problem, visible]);

  function handleRetry() {
    void queryClient.refetchQueries({
      type: 'active',
      predicate: (query) => query.state.status === 'error',
    });
  }

  const { icon: Icon, tone, message } = MODE_STYLES[mode];

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-[max(1rem,env(safe-area-inset-bottom))] z-50 flex justify-center px-4 transition-all duration-300 ease-out',
        visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0',
      )}
      role="status"
      aria-live="polite"
      inert={!visible}
    >
      <div
        className={cn(
          'flex max-w-full items-center gap-2 rounded-full bg-foreground py-1.5 pl-2 text-sm text-background shadow-xl',
          problem ? 'pr-1.5' : 'pr-3',
        )}
      >
        <span
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-full',
            STATUS_COLOR_CLASS[tone],
          )}
        >
          <Icon className="size-3.5" aria-hidden />
        </span>
        <span className="min-w-0 truncate font-medium">{message}</span>
        {problem && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 rounded-full bg-background/15 px-3 text-background hover:bg-background/25 hover:text-background"
              onClick={handleRetry}
            >
              Retry
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="size-7 text-background hover:bg-background/15 hover:text-background"
              aria-label="Hide"
              onClick={() => setVisible(false)}
            >
              <XIcon className="size-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
